import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IGateway, MaybeAssociatedClientDevice } from "@grabcad/company-server-shared/dist/types";
import { getGatewayList, getOrphanedClients } from "../../../../zeus-client/gatewaysApis";
import { ApplicationContext } from "../../../../components/ApplicationProvider";
import { Notifier } from "../../../../view/Notifier";
import { findExternalCompanyIdKey } from "../../../Gateway/renderOwnedByLabels";
import { useCompanyAsAdminQuery } from "../../../../graphql";
import { useCompanyLicensesQuery } from "../../../../graphql/Queries/Licenses/GetCompanyLicenses";
import {
    companyItem,
    useGetCompaniesByExternalId,
} from "../../../../graphql/Queries/Companies/CompanyListWithExternalId";

interface UseGatewayManagerResult {
    loading: boolean;
    gateways: IGateway[];
    orphanedClients: MaybeAssociatedClientDevice[];
    companiesListItem: companyItem[];
    activePrinterSerial?: string;
    deviceFound: boolean;
    hasGateway: boolean;
    hasOrphanedDevice: boolean;
    isCSE: boolean;
    isReseller: boolean;
    isCSEOrReseller: boolean;
    searchGateways: (printerSerial?: string) => Promise<void>;
    refreshGateways: () => Promise<void>;
}

const useGatewayManager = (): UseGatewayManagerResult => {
    const { t } = useContext(ApplicationContext);

    const { allEnabledFeatureKeys, loadingLicenses } = useCompanyLicensesQuery();
    const { company, companyLoading } = useCompanyAsAdminQuery();

    const isCSE = allEnabledFeatureKeys.has("zeus_stratasys_cse");
    const isReseller = company?.companyType === "reseller";
    const isCSEOrReseller = isCSE || isReseller;

    const [loading, setLoading] = useState<boolean>(true);

    const [gateways, setGateways] = useState<IGateway[]>([]);
    const [orphanedClients, setOrphanedClients] = useState<MaybeAssociatedClientDevice[]>([]);
    const [companiesListItem, setCompaniesListItem] = useState<companyItem[]>([]);

    const [activePrinterSerial, setActivePrinterSerial] = useState<string>();
    const [externalId, setExternalId] = useState("");

    const [deviceFound, setDeviceFound] = useState(false);
    const [hasOrphanedDevice, setHasOrphanedDevice] = useState(false);

    const { data: companiesList } = useGetCompaniesByExternalId(externalId);

    useEffect(() => {
        const companies = companiesList?.getCompaniesByExternalId?.companies;

        if (companies) {
            setCompaniesListItem(companies);
        }
    }, [companiesList]);

    const searchGateways = useCallback(
        async (printerSerial?: string): Promise<void> => {
            setLoading(true);
            setExternalId("");
            setActivePrinterSerial(printerSerial);

            try {
                const fetchGatewayData = async (
                    serial?: string
                ): Promise<
                    [
                        PromiseSettledResult<IGateway[]>,
                        PromiseSettledResult<MaybeAssociatedClientDevice[]>
                    ]
                > => Promise.allSettled([getGatewayList(serial), getOrphanedClients(serial)]);

                let [gatewayResult, clientsResult] = await fetchGatewayData(printerSerial);

                let serialSearchActive = Boolean(printerSerial?.trim());

                if (
                    printerSerial &&
                    gatewayResult.status === "rejected" &&
                    clientsResult.status === "rejected"
                ) {
                    [gatewayResult, clientsResult] = await fetchGatewayData();

                    Notifier.fail(t("gateways.printer_serial_not_found"));

                    serialSearchActive = false;
                }

                const fetchedGateways =
                    gatewayResult.status === "fulfilled" ? gatewayResult.value : [];

                const rawClients = clientsResult.status === "fulfilled" ? clientsResult.value : [];

                const orphanedDevices = rawClients.filter(
                    (client) => !Object.prototype.hasOwnProperty.call(client, "coreDeviceThingName")
                );

                setGateways(fetchedGateways);
                setOrphanedClients(orphanedDevices);

                if (serialSearchActive && printerSerial) {
                    const serial = printerSerial.trim();

                    const printerExists = rawClients.some((client) => client.thingName === serial);

                    const orphanExists = orphanedDevices.some(
                        (client) => client.thingName === serial
                    );

                    setDeviceFound(printerExists);
                    setHasOrphanedDevice(orphanExists);

                    if (printerExists) {
                        const externalCompanyId = findExternalCompanyIdKey(
                            serial,
                            rawClients,
                            fetchedGateways
                        );

                        setExternalId(externalCompanyId ?? "");
                    }
                } else {
                    setDeviceFound(false);
                    setHasOrphanedDevice(false);
                }
            } catch (error) {
                console.error("Failed to load gateways", error);

                Notifier.fail(t("gateways.gateway_request_error"));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const refreshGateways = useCallback(async (): Promise<void> => {
        await searchGateways(activePrinterSerial);
    }, [activePrinterSerial, searchGateways]);

    const initialLoadRef = useRef(false);

    useEffect(() => {
        if (loadingLicenses || companyLoading || initialLoadRef.current) {
            return;
        }

        initialLoadRef.current = true;

        if (!isCSEOrReseller) {
            void searchGateways();
        } else {
            setLoading(false);
        }
    }, [loadingLicenses, companyLoading, isCSEOrReseller, searchGateways]);

    return {
        loading,
        gateways,
        orphanedClients,
        companiesListItem,
        activePrinterSerial,
        deviceFound,
        hasGateway: gateways.length > 0,
        hasOrphanedDevice,
        isCSE,
        isReseller,
        isCSEOrReseller,
        searchGateways,
        refreshGateways,
    };
};

export default useGatewayManager;
